import { useState } from "react";
import { Btn, Modal, Input, FieldLabel } from "components/ui/index";
import { T } from "theme/index";
import { fmtShort, fmtDec, fmtNum, allowDecInput, parseDec, parseNum, uid, todayStr } from "utils/format";
import { TYPE_META } from "constants/index";
import { fetchPrice } from "utils/calc";
import { getQty, calcSalePreview } from "engine/investmentSales";

// ── Investment Modal ──────────────────────────────────────────────────────────
// mode: "new" (instrumen baru) | "edit" (ubah posisi) | "buy" (tambah pembelian → averaging) | "sell" (jual sebagian/semua posisi)
export default function InvModal({ inv, mode = "new", existingGroups = [], onSave, onSell, onClose }) {
  const emptyForm = { id:uid(), name:"", group:"", type:"saham_idx", ticker:"", coinId:"", lot:"", unit:"", avgPrice:"", currentPrice:"", notes:"" };
  const base = inv ? { ...emptyForm, ...inv, lot:String(inv.lot||""), unit:fmtDec(inv.unit||""), avgPrice:fmtDec(inv.avgPrice||""), currentPrice:fmtDec(inv.currentPrice||"") } : emptyForm;

  // Untuk mode "buy": form kosong untuk pembelian baru, tapi tipe & identitas mengikuti instrumen
  const buyInit = inv ? { ...emptyForm, type:inv.type, name:inv.name, group:inv.group, ticker:inv.ticker, coinId:inv.coinId, currentPrice:fmtDec(inv.currentPrice||""), lot:"", unit:"", avgPrice:"" } : emptyForm;

  const [f, setF]           = useState(mode === "buy" ? buyInit : base);
  const [fetching, setFetching] = useState(false);
  const [fetchErr, setFetchErr] = useState("");
  const [addingGroup, setAddingGroup] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]:v }));

  // ── Mode "sell" ──
  const [sellForm, setSellForm] = useState({ qty:"", sellPrice:fmtDec(inv?.currentPrice||""), date:todayStr(), notes:"" });
  const setSell = (k, v) => setSellForm(p => ({ ...p, [k]:v }));

  const needsTicker  = f.type === "saham_idx" || f.type === "saham_us" || f.type === "forex";
  const needsCoinId  = f.type === "crypto";
  const usesLot      = f.type === "saham_idx";
  const meta         = TYPE_META[f.type] || TYPE_META.lainnya;

  const handleFetch = async () => {
    setFetching(true); setFetchErr("");
    const price = await fetchPrice({ ...f });
    setFetching(false);
    if (price) set("currentPrice", fmtDec(price));
    else setFetchErr("Gagal fetch harga. Cek ticker/coin ID atau isi manual.");
  };

  const qtyVal   = usesLot ? parseNum(f.lot) : parseDec(f.unit);
  const qtyMult  = usesLot ? 100 : 1;
  const invested = qtyVal * qtyMult * parseDec(f.avgPrice);
  const current  = qtyVal * qtyMult * parseDec(f.currentPrice);

  // Preview hasil averaging (mode buy)
  const buyPreview = (() => {
    if (mode !== "buy" || !inv) return null;
    const oldQty = usesLot ? (inv.lot||0) : (inv.unit||0);
    const addQty = usesLot ? parseNum(f.lot) : parseDec(f.unit);
    const oldAvg = inv.avgPrice || 0;
    const addPrice = parseDec(f.avgPrice);
    if (addQty <= 0) return null;
    const newQty = oldQty + addQty;
    const newAvg = ((oldQty * oldAvg) + (addQty * addPrice)) / newQty;
    return { oldQty, addQty, newQty, oldAvg, newAvg };
  })();

  // Preview realized gain (mode sell)
  const availableQty = inv ? getQty(inv) : 0;
  const sellQty       = usesLot ? parseNum(sellForm.qty) : parseDec(sellForm.qty);
  const sellPriceVal  = parseDec(sellForm.sellPrice);
  const sellQtyValid  = sellQty > 0 && sellQty <= availableQty;
  const sellPreview   = (mode === "sell" && inv && sellQtyValid)
    ? calcSalePreview(inv, sellQty, sellPriceVal)
    : null;

  const save = () => {
    if (!f.name) return;

    if (mode === "sell" && inv && sellPreview) {
      const sale = {
        id: uid(),
        investmentId: inv.id,
        name: inv.name, type: inv.type, group: inv.group,
        date: sellForm.date,
        qty: sellQty,
        sellPrice: sellPriceVal,
        proceeds: sellPreview.proceeds,
        costBasis: sellPreview.costBasis,
        realizedGain: sellPreview.realizedGain,
        notes: sellForm.notes,
      };
      const updatedInv = { ...inv, lot: usesLot ? sellPreview.remainingQty : inv.lot, unit: usesLot ? inv.unit : sellPreview.remainingQty };
      onSell(sale, updatedInv);
      onClose();
      return;
    }

    if (mode === "buy" && inv && buyPreview) {
      // Gabung ke posisi lama dengan averaging
      const merged = {
        ...inv,
        lot:  usesLot ? buyPreview.newQty : inv.lot,
        unit: usesLot ? inv.unit : buyPreview.newQty,
        avgPrice: buyPreview.newAvg,
        currentPrice: parseDec(f.currentPrice) || inv.currentPrice,
      };
      onSave(merged);
      onClose();
      return;
    }

    onSave({
      ...f,
      lot:parseNum(f.lot),
      unit:parseDec(f.unit),
      avgPrice:parseDec(f.avgPrice),
      currentPrice:parseDec(f.currentPrice),
    });
    onClose();
  };

  if (mode === "sell" && inv) {
    const qtyLabel = usesLot ? "Lot" : (f.type==="emas"?"Gram":f.type==="forex"?"Valas":"Unit");
    return (
      <Modal title={`Jual — ${inv.name}`} onClose={onClose}>
        <div style={{ background:meta.color+"12", border:`1px solid ${meta.color}33`, borderRadius:10, padding:"10px 14px", marginBottom:16, fontSize:12, color:T.text }}>
          {meta.icon} <b>{inv.name}</b> {inv.ticker && <span style={{ color:T.muted, fontFamily:"monospace" }}>· {inv.ticker}</span>}<br/>
          <span style={{ color:T.muted }}>Posisi tersedia: {usesLot ? `${fmtDec(availableQty)} lot` : `${fmtDec(availableQty)} ${qtyLabel.toLowerCase()}`} @ avg {fmtDec(inv.avgPrice)}</span>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 14px" }}>
          <Input label={`${qtyLabel} Dijual`} type="text" inputMode={usesLot?"numeric":"decimal"}
            value={usesLot ? fmtNum(sellForm.qty) : sellForm.qty}
            onChange={e => setSell("qty", usesLot ? e.target.value.replace(/\D/g,"") : allowDecInput(e.target.value))}
            placeholder="0" />
          <Input label="Harga Jual / satuan" type="text" inputMode="decimal"
            value={sellForm.sellPrice} onChange={e => setSell("sellPrice", allowDecInput(e.target.value))} placeholder="0" />
          <Input label="Tanggal Jual" type="date" value={sellForm.date} onChange={e => setSell("date", e.target.value)} />
          <div style={{ gridColumn:"1/-1" }}>
            <Input label="Catatan" value={sellForm.notes} onChange={e => setSell("notes", e.target.value)} placeholder="Catatan tambahan..." />
          </div>
        </div>

        {sellForm.qty && !sellQtyValid && (
          <div style={{ color:T.red, fontSize:12, marginTop:-4, marginBottom:12 }}>⚠️ Qty jual gak boleh melebihi posisi tersedia ({fmtDec(availableQty)}).</div>
        )}

        {sellPreview && (
          <div style={{ background:sellPreview.realizedGain>=0?T.green+"12":T.red+"12", border:`1px solid ${sellPreview.realizedGain>=0?T.green:T.red}33`, borderRadius:10, padding:14, marginBottom:14 }}>
            <div style={{ color:T.muted, fontSize:10, fontWeight:700, letterSpacing:"0.06em", marginBottom:8 }}>PREVIEW HASIL JUAL</div>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:13, marginBottom:4 }}>
              <span style={{ color:T.muted }}>Hasil Jual</span>
              <span style={{ color:T.text, fontFamily:"monospace" }}>{fmtShort(sellPreview.proceeds)}</span>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:13, marginBottom:4 }}>
              <span style={{ color:T.muted }}>Modal (cost basis)</span>
              <span style={{ color:T.text, fontFamily:"monospace" }}>{fmtShort(sellPreview.costBasis)}</span>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:13, marginBottom:4 }}>
              <span style={{ color:T.muted }}>Realized Gain/Loss</span>
              <span style={{ color:sellPreview.realizedGain>=0?T.green:T.red, fontFamily:"monospace", fontWeight:700 }}>{sellPreview.realizedGain>=0?"+":""}{fmtShort(sellPreview.realizedGain)}</span>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:13 }}>
              <span style={{ color:T.muted }}>Sisa Posisi</span>
              <span style={{ color:T.text, fontFamily:"monospace" }}>{fmtDec(sellPreview.remainingQty)} {qtyLabel.toLowerCase()}{sellPreview.remainingQty<=0 ? " — posisi habis" : ""}</span>
            </div>
          </div>
        )}

        <div style={{ display:"flex", justifyContent:"flex-end", gap:8 }}>
          <Btn variant="ghost" onClick={onClose}>Batal</Btn>
          <Btn onClick={save} disabled={!sellPreview}>💰 Jual</Btn>
        </div>
      </Modal>
    );
  }

  const title = mode === "buy" ? `Tambah Pembelian — ${inv?.name}` : inv ? "Edit Instrumen" : "Tambah Instrumen";

  return (
    <Modal title={title} onClose={onClose} wide>
      {/* Tipe — hanya bisa diubah saat instrumen baru */}
      {mode === "new" && (
        <div style={{ marginBottom:14 }}>
          <FieldLabel>Tipe Aset</FieldLabel>
          <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
            {Object.entries(TYPE_META).map(([key, m]) => (
              <button key={key} onClick={() => set("type", key)}
                style={{ padding:"7px 11px", borderRadius:8, cursor:"pointer", fontWeight:600, fontSize:12, border:`1px solid ${f.type===key?m.color:T.border}`, background:f.type===key?m.color+"20":T.surface, color:f.type===key?m.color:T.muted, WebkitTapHighlightColor:"transparent", display:"flex", alignItems:"center", gap:5 }}>
                {m.icon} {m.label}
              </button>
            ))}
          </div>
          <div style={{ color:T.muted, fontSize:11, marginTop:6 }}>{meta.desc}</div>
        </div>
      )}

      {mode === "buy" && (
        <div style={{ background:meta.color+"12", border:`1px solid ${meta.color}33`, borderRadius:10, padding:"10px 14px", marginBottom:16, fontSize:12, color:T.text }}>
          {meta.icon} <b>{inv?.name}</b> {inv?.ticker && <span style={{ color:T.muted, fontFamily:"monospace" }}>· {inv.ticker}</span>}<br/>
          <span style={{ color:T.muted }}>Posisi saat ini: {usesLot ? `${inv.lot} lot` : `${fmtDec(inv.unit)} unit`} @ avg {fmtDec(inv.avgPrice)}</span>
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 14px" }}>
        {/* Nama & kelompok — disembunyikan di mode buy (sudah fix) */}
        {mode !== "buy" && (
          <>
            <div style={{ gridColumn:"1/-1" }}>
              <Input label="Nama Instrumen" value={f.name} onChange={e => set("name", e.target.value)} placeholder={needsTicker?"Bank Central Asia":needsCoinId?"Bitcoin":"Nama aset..."} />
            </div>
            {/* Kelompok / Portfolio — dropdown existing + tambah baru */}
            <div style={{ marginBottom:14 }}>
              <div style={{ color:T.muted, fontSize:11, fontWeight:700, letterSpacing:"0.07em", marginBottom:6, textTransform:"uppercase" }}>Kelompok / Portfolio</div>
              {addingGroup ? (
                <div style={{ display:"flex", gap:6 }}>
                  <input autoFocus value={f.group} onChange={e => set("group", e.target.value)} placeholder="Nama kelompok baru..."
                    style={{ flex:1, background:T.surface, border:`1px solid ${T.gold}`, borderRadius:8, color:T.text, fontSize:15, padding:"10px 12px", outline:"none" }}
                  />
                  <button onClick={() => { if (!f.group) setAddingGroup(false); else setAddingGroup(false); }}
                    style={{ background:T.gold, border:"none", borderRadius:8, color:T.bg, fontWeight:700, fontSize:13, padding:"0 14px", cursor:"pointer" }}>✓</button>
                  <button onClick={() => { set("group", inv?.group||""); setAddingGroup(false); }}
                    style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:8, color:T.muted, fontSize:13, padding:"0 10px", cursor:"pointer" }}>✕</button>
                </div>
              ) : (
                <select value={f.group} onChange={e => { if (e.target.value === "__new__") { set("group",""); setAddingGroup(true); } else set("group", e.target.value); }}
                  style={{ width:"100%", background:T.surface, border:`1px solid ${T.border}`, borderRadius:8, color:f.group?T.text:T.muted, fontSize:15, padding:"10px 12px", outline:"none", cursor:"pointer", boxSizing:"border-box" }}>
                  <option value="">— Pilih kelompok —</option>
                  {existingGroups.map(g => <option key={g} value={g}>{g}</option>)}
                  <option value="__new__">➕ Tambah kelompok baru...</option>
                </select>
              )}
            </div>

            {needsTicker && (
              <div>
                <FieldLabel>{f.type==="forex"?"Kode Mata Uang":`Ticker ${f.type==="saham_idx"?"IDX":"US"}`}</FieldLabel>
                <div style={{ display:"flex", gap:6 }}>
                  <input value={f.ticker} onChange={e => set("ticker", e.target.value.toUpperCase())}
                    placeholder={f.type==="saham_idx"?"BBCA":f.type==="forex"?"USD / EUR / JPY":"AAPL"}
                    style={{ flex:1, background:T.surface, border:`1px solid ${T.border}`, borderRadius:8, color:T.text, fontSize:16, padding:"11px 12px", outline:"none", boxSizing:"border-box", textTransform:"uppercase" }}
                  />
                  <button onClick={handleFetch} disabled={!f.ticker || fetching}
                    style={{ background:T.gold+"22", border:`1px solid ${T.gold}44`, borderRadius:8, color:T.gold, cursor:"pointer", padding:"0 12px", fontSize:13, fontWeight:700, whiteSpace:"nowrap", opacity:(!f.ticker||fetching)?0.4:1, WebkitTapHighlightColor:"transparent" }}>
                    {fetching ? "⏳" : "🔄 Fetch"}
                  </button>
                </div>
                {f.type==="forex" && <div style={{ color:T.muted, fontSize:10, marginTop:4 }}>Fetch akan mengambil kurs {f.ticker||"USD"} → IDR otomatis</div>}
              </div>
            )}
            {needsCoinId && (
              <div>
                <FieldLabel>CoinGecko ID</FieldLabel>
                <div style={{ display:"flex", gap:6 }}>
                  <input value={f.coinId} onChange={e => set("coinId", e.target.value.toLowerCase())}
                    placeholder="bitcoin / ethereum"
                    style={{ flex:1, background:T.surface, border:`1px solid ${T.border}`, borderRadius:8, color:T.text, fontSize:14, padding:"11px 12px", outline:"none", boxSizing:"border-box" }}
                  />
                  <button onClick={handleFetch} disabled={!f.coinId || fetching}
                    style={{ background:T.gold+"22", border:`1px solid ${T.gold}44`, borderRadius:8, color:T.gold, cursor:"pointer", padding:"0 12px", fontSize:13, fontWeight:700, opacity:(!f.coinId||fetching)?0.4:1, WebkitTapHighlightColor:"transparent" }}>
                    {fetching ? "⏳" : "🔄 Fetch"}
                  </button>
                </div>
                <div style={{ color:T.muted, fontSize:10, marginTop:4 }}>ID dari coingecko.com/en/coins/{f.coinId||"bitcoin"}</div>
              </div>
            )}
          </>
        )}

        {fetchErr && <div style={{ gridColumn:"1/-1", color:T.orange, fontSize:12, marginTop:-4, marginBottom:8 }}>⚠️ {fetchErr}</div>}

        {/* Qty — di mode buy ini "jumlah yang dibeli" */}
        {usesLot ? (
          <Input label={mode==="buy"?"Lot Dibeli":"Jumlah Lot (1 lot = 100 lembar)"} type="text" inputMode="numeric"
            value={fmtNum(f.lot)} onChange={e => set("lot", e.target.value.replace(/\D/g,""))} placeholder="0" />
        ) : (
          <Input label={mode==="buy"?(f.type==="emas"?"Gram Dibeli":f.type==="forex"?"Jumlah Valas Dibeli":"Unit Dibeli"):(f.type==="emas"?"Gram":f.type==="forex"?"Jumlah Valas (mis. 100 USD)":"Unit / Koin")} type="text" inputMode="decimal"
            value={f.unit} onChange={e => set("unit", allowDecInput(e.target.value))} placeholder="0" />
        )}

        {/* Harga beli (desimal) */}
        <Input label={mode==="buy"?(f.type==="forex"?"Kurs Beli (IDR per 1 valas)":"Harga Beli / "+(usesLot?"lembar":"unit")):(f.type==="forex"?"Avg Kurs Beli (IDR per 1 valas)":(usesLot?"Avg Buy Price / lembar":"Avg Buy Price / unit"))} type="text" inputMode="decimal"
          value={f.avgPrice} onChange={e => set("avgPrice", allowDecInput(e.target.value))} placeholder="0" />

        {/* Harga sekarang */}
        <div style={{ gridColumn:"1/-1" }}>
          <FieldLabel>Harga Sekarang {needsTicker||needsCoinId?"(auto-fetch / manual)":""}</FieldLabel>
          <input type="text" inputMode="decimal" value={f.currentPrice} onChange={e => set("currentPrice", allowDecInput(e.target.value))} placeholder="0"
            style={{ width:"100%", background:f.currentPrice?T.green+"15":T.surface, border:`1px solid ${f.currentPrice?T.green+"44":T.border}`, borderRadius:8, color:T.text, fontSize:16, padding:"11px 12px", outline:"none", boxSizing:"border-box" }}
          />
        </div>

        {mode !== "buy" && (
          <div style={{ gridColumn:"1/-1" }}>
            <Input label="Catatan" value={f.notes} onChange={e => set("notes", e.target.value)} placeholder="Catatan tambahan..." />
          </div>
        )}
      </div>

      {/* Preview averaging (mode buy) */}
      {buyPreview && (
        <div style={{ background:T.surface, borderRadius:10, padding:14, marginTop:6, marginBottom:14 }}>
          <div style={{ color:T.muted, fontSize:10, fontWeight:700, letterSpacing:"0.06em", marginBottom:8 }}>HASIL SETELAH PEMBELIAN</div>
          <div style={{ display:"flex", justifyContent:"space-between", fontSize:13, marginBottom:4 }}>
            <span style={{ color:T.muted }}>Total {usesLot?"lot":"unit"}</span>
            <span style={{ color:T.text, fontFamily:"monospace" }}>{fmtDec(buyPreview.oldQty)} → <b style={{ color:T.green }}>{fmtDec(buyPreview.newQty)}</b></span>
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", fontSize:13 }}>
            <span style={{ color:T.muted }}>Avg price baru</span>
            <span style={{ color:T.text, fontFamily:"monospace" }}>{fmtDec(buyPreview.oldAvg)} → <b style={{ color:T.gold }}>{fmtDec(buyPreview.newAvg)}</b></span>
          </div>
        </div>
      )}

      {/* Summary preview (mode new/edit) */}
      {mode !== "buy" && (invested > 0 || current > 0) && (
        <div style={{ background:T.surface, borderRadius:10, padding:14, marginBottom:14, display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
          <div><div style={{ color:T.muted, fontSize:10, marginBottom:3 }}>MODAL</div><div style={{ color:T.blue, fontFamily:"monospace", fontWeight:700 }}>{fmtShort(invested)}</div></div>
          <div><div style={{ color:T.muted, fontSize:10, marginBottom:3 }}>NILAI</div><div style={{ color:T.gold, fontFamily:"monospace", fontWeight:700 }}>{fmtShort(current)}</div></div>
          <div><div style={{ color:T.muted, fontSize:10, marginBottom:3 }}>G/L</div><div style={{ color:(current-invested)>=0?T.green:T.red, fontFamily:"monospace", fontWeight:700 }}>{(current-invested)>=0?"+":""}{fmtShort(current-invested)}</div></div>
        </div>
      )}

      <div style={{ display:"flex", justifyContent:"flex-end", gap:8 }}>
        <Btn variant="ghost" onClick={onClose}>Batal</Btn>
        <Btn onClick={save} disabled={!f.name || (mode==="buy" && !buyPreview)}>💾 Simpan</Btn>
      </div>
    </Modal>
  );
}


